// ────────────────────────────────────────────────────────────────────────────
// Between the stored policy and the workflow the screen edits.
//
// The split is deliberate and it is the same one the backend makes: the STATE
// MACHINE is code — which states a track has, their labels, the moves between
// them, who performs each move — while the POLICY is what an organisation
// decides: the conditions on each move, how each state behaves, and the
// cancellation rules.
//
// Storing the machine would freeze a product decision inside customer data: a
// renamed state or a new one would never reach an organisation that had pressed
// Save. So the structure lives in `mockWorkflow.ts` and only the policy travels.
// ────────────────────────────────────────────────────────────────────────────
import type {
  WorkflowPolicyConfig,
  WorkflowRequirementConfig,
} from '@my/tms-api';
import type {
  Requirement,
  Severity,
  Polarity,
  Workflow,
  WorkflowTransition,
} from './mockWorkflow';

const SEVERITIES: Severity[] = ['ignore', 'warn', 'stop'];
const POLARITIES: Polarity[] = ['require', 'prevent'];

/** `"{from}->{to}"` — a transition has no id of its own. */
export function transitionKey(t: Pick<WorkflowTransition, 'from' | 'to'>): string {
  return `${t.from}->${t.to}`;
}

/**
 * Read one stored condition, dropping anything malformed.
 *
 * <p>
 * A bad severity is dropped rather than defaulted: defaulting it to `warn`
 * would leave a rule on screen that looks configured while doing something
 * other than what was saved, and defaulting to `stop` would block work over a
 * data error. Dropping it makes the loss visible on the screen.
 * </p>
 */
function toRequirement(raw: WorkflowRequirementConfig): Requirement | null {
  if (!raw?.kind || !SEVERITIES.includes(raw.severity)) return null;
  const polarity = raw.polarity ?? undefined;
  if (polarity != null && !POLARITIES.includes(polarity)) return null;
  return {
    kind: raw.kind as Requirement['kind'],
    severity: raw.severity,
    ...(polarity ? { polarity } : {}),
    ...(raw.param != null ? { param: raw.param } : {}),
  };
}

function toRequirements(raw: WorkflowRequirementConfig[] | undefined): Requirement[] {
  return (raw ?? []).map(toRequirement).filter((r): r is Requirement => r !== null);
}

/**
 * Lay a stored policy over the structural workflow.
 *
 * <p>
 * Keys the current build does not have are ignored — they name a state or move
 * an older version had. Keys the policy does not mention keep whatever the
 * structure carries, which is how a newly added state behaves sensibly before
 * anyone has configured it.
 * </p>
 */
export function applyPolicy(workflow: Workflow, config: WorkflowPolicyConfig): Workflow {
  const states = workflow.states.map((st) => {
    const stored = config.states?.[st.key];
    if (!stored) return st;
    return {
      ...st,
      behaviour: {
        plannable: stored.plannable,
        editable: stored.editable,
        // `invoiceable` is absent on non-order tracks, and absent means "keep
        // whatever the structure said" rather than "false" — a null would
        // otherwise silently close invoicing on every state.
        ...(stored.invoiceable == null
          ? { invoiceable: st.behaviour.invoiceable }
          : { invoiceable: stored.invoiceable }),
      },
    };
  });

  const transitions = workflow.transitions.map((t) => {
    const stored = config.transitions?.[transitionKey(t)];
    // `undefined` means untouched; an empty array means the operator removed
    // every condition, which is a real answer and must survive.
    return stored === undefined ? t : { ...t, requirements: toRequirements(stored) };
  });

  return {
    ...workflow,
    states,
    transitions,
    cancelRequirements: toRequirements(config.cancelRequirements),
  };
}

/** Extract what the backend stores — the policy, never the machine. */
export function toPolicyConfig(workflow: Workflow): WorkflowPolicyConfig {
  const states: WorkflowPolicyConfig['states'] = {};
  for (const st of workflow.states) {
    states[st.key] = {
      plannable: st.behaviour.plannable,
      editable: st.behaviour.editable,
      ...(st.behaviour.invoiceable === undefined
        ? {}
        : { invoiceable: st.behaviour.invoiceable }),
    };
  }

  const transitions: WorkflowPolicyConfig['transitions'] = {};
  for (const t of workflow.transitions) {
    // Every move is sent, including those with no conditions: omitting them
    // would let a shipped default reappear after an operator had cleared it.
    transitions[transitionKey(t)] = t.requirements.map((r) => ({
      kind: r.kind,
      severity: r.severity,
      polarity: r.polarity ?? null,
      param: r.param ?? null,
    }));
  }

  return {
    states,
    transitions,
    cancelRequirements: workflow.cancelRequirements.map((r) => ({
      kind: r.kind,
      severity: r.severity,
      polarity: r.polarity ?? null,
      param: r.param ?? null,
    })),
  };
}
